import _ from "underscore";
import axios from "axios";
import {
  getErrorMessage,
  getFormattedDenomination,
} from "../components/superAdmin/catalog/lib/helpers";

export const createCatalogItem = async (data) => {
  const catalogItemRes = await axios.post(
    "/rewards_catalog/gateway/api/v1/items",
    { item: data.catalogItem }
  );

  if (catalogItemRes.status === 201) {
    const denominations = data.denominations.map((denom) => {
      return {
        ...denom,
        item_id: catalogItemRes.data.data.id,
      };
    });

    const denominationRes = await createDenominations(denominations);

    if (denominationRes.status === "success") {
      return catalogItemRes;
    } else {
      throw {
        response: {
          data: {
            catalogItemId: catalogItemRes.data.data.id,
            catalogItemName: catalogItemRes.data.data.brandName,
            denominationError: true,
            errors: denominationRes.errorMessages,
          },
        },
      };
    }
  } else {
    return catalogItemRes;
  }
};

const createDenomination = (denomination) => {
  return axios.post("/rewards_catalog/gateway/api/v1/denominations", {
    denomination: { ...denomination },
  });
};

const createDenominations = async (denominations) => {
  const errorMessages = [];
  const responses = await Promise.allSettled(
    denominations.map((denom) => createDenomination(denom))
  );

  responses.forEach((response, index) => {
    if (response.status === "rejected") {
      errorMessages.push(
        `Failed to create the ${
          denominations[index].price_in_subunits / 100
        } denomination: ${response.reason.response.data.errors.join(" ")}`
      );
    }
  });

  return {
    errorMessages,
    status: errorMessages.length ? "error" : "success",
  };
};

export const deleteCatalogItem = (catalogItemId) => {
  return axios.delete(`/rewards_catalog/gateway/api/v1/items/${catalogItemId}`);
};

const deleteDenomination = (denominationId) => {
  return axios.delete(
    `/rewards_catalog/gateway/api/v1/denominations/${denominationId}`
  );
};

export const getCatalogItems = (params) => {
  return axios.get("/rewards_catalog/gateway/api/v1/items", {
    params: { ...params },
    paramsSerializer: function (params) {
      return jQuery.param(params);
    },
  });
};

export const getCatalogItemDetails = (id) => {
  return axios.get(`/rewards_catalog/gateway/api/v1/items/${id}`);
};

export const getCategories = () => {
  return axios.get("/rewards_catalog/gateway/api/v1/categories");
};

export const getCountries = () => {
  return axios.get("/rewards_catalog/gateway/api/v1/countries");
};

export const getCurrencies = () => {
  return axios.get("/rewards_catalog/gateway/api/v1/currencies");
};

export const getRedemptionMethods = () => {
  return axios.get("/rewards_catalog/gateway/api/v1/redemption_methods");
};

export const updateCatalogItem = async (id, data) => {
  const catalogItemRes = await axios.put(
    `/rewards_catalog/gateway/api/v1/items/${id}`,
    {
      item: { ...data.catalogItem },
    }
  );

  if (catalogItemRes.status === 200) {
    const oldDenominations = [...catalogItemRes.data.data.denominations];
    const newDenominations = [...data.denominations];
    const updatedDenominations = [];
    const denominationErrors = [];

    // NOTE: Check to see if old denominations are in new denomination list
    // if not, delete them
    for await (const oldDenom of oldDenominations) {
      const found = newDenominations.find(
        (newDenom) => newDenom.id === oldDenom.id
      );
      if (!found) {
        try {
          await deleteDenomination(oldDenom.id);
        } catch (error) {
          const errorMessage = getErrorMessage(error);
          // NOTE: if errors, the denominatoin still exists
          updatedDenominations.push({ ...oldDenom });
          denominationErrors.push(
            `Failed to delete the ${
              oldDenom.price_in_subunits / 100
            } denomination:
						${errorMessage}`
          );
        }
      }
    }

    // Check to see if new denominations already existed before and update or create if it is new
    for await (const newDenom of newDenominations) {
      const found = oldDenominations.find(
        (oldDenom) => oldDenom.id === newDenom.id
      );

      if (!found) {
        try {
          const denomRes = await createDenomination({
            ...newDenom,
            item_id: catalogItemRes.data.data.id,
          });
          updatedDenominations.push({ ...denomRes.data.data });
        } catch (error) {
          const errorMessage = getErrorMessage(error);
          denominationErrors.push(
            `Failed to create the ${
              newDenom.price_in_subunits / 100
            } denomination:
						${errorMessage}`
          );
        }
      } else {
        const oldDenomination = getFormattedDenomination(
          found,
          catalogItemRes.data.data.currency
        );
        const newEqualsOld = _.isEqual(newDenom, oldDenomination);
        // NOTE: only update if it changed
        if (!newEqualsOld) {
          try {
            const denomRes = await updateDenomination({ ...newDenom });
            updatedDenominations.push({ ...denomRes.data.data });
          } catch (error) {
            const errorMessage = getErrorMessage(error);
            denominationErrors.push(
              `Failed to update the ${
                newDenom.price_in_subunits / 100
              } denomination:
              ${errorMessage}`
            );
          }
        } else {
          // NOTE: updatedDenominations are returned so return original if not updated
          updatedDenominations.push({ ...found });
        }
      }
    }

    if (!denominationErrors.length) {
      const response = { ...catalogItemRes };
      response.data.data.denominations = [...updatedDenominations];
      return response;
    } else {
      throw {
        response: {
          data: {
            catalogItemId: catalogItemRes.data.data.id,
            catalogItemName: catalogItemRes.data.data.brandName,
            denominationError: true,
            errors: denominationErrors,
          },
        },
      };
    }
  } else {
    return catalogItemRes;
  }
};

export const updateDenomination = (denomination) => {
  return axios.put(
    `/rewards_catalog/gateway/api/v1/denominations/${denomination.id}`,
    {
      denomination: { ...denomination },
    }
  );
};

export const updateDenominations = async (denominations) => {
  const errorMessages = [];
  const responses = await Promise.allSettled(
    denominations.map((denom) => updateDenomination(denom))
  );

  responses.forEach((response, index) => {
    if (response.status === "rejected") {
      errorMessages.push(
        `Failed to update the ${
          denominations[index].price_in_subunits / 100
        } denomination for ${
          denominations[index].item_brand_name
        }: ${response.reason.response.data.errors.join(" ")}`
      );
    }
  });

  if (errorMessages.length) {
    throw {
      errorMessages,
      status: "error",
    };
  }
  return { status: "success" };
};
